import { ObservationService } from 'src/app/shared/observation/observation.service';
import { Component, Input, Output, EventEmitter, ViewChild, OnInit } from '@angular/core';
import { Table as PrimeDataTable } from 'primeng/table';
import { NavigationService } from '../../../shared/navigation/navigation.service';
import { UserService } from '../../../users/shared/user.service';
import {
  MassCommService,
  Message,
  ManualRecipient,
  CommunicationRecord,
  DraftProfileMin,
  ReplaceEmailAddressOptionEnum,
  ReplaceWithEmailTypeConfig,
} from '../../shared/index';
import { PagedResult } from '../../../shared/model/generic-paged-result.model';
import { DatagridService } from '../../../shared/index';
import { ProfileService } from '../../../profiles/shared/profile.service';
import { EmailAddressType } from '../../../profiles/shared/email-address-type.model';
import { ProfileSearchResult } from '../../../profiles/search/profile-search-result.model';
import { ProfileAdvancedSearchParams } from '../../../profiles/search/profile-advanced-search-params.model';
import { ProfileSearchService } from '../../../profiles/search/profile-search.service';
import { RelatedProfileOptionsModel } from '../../../profiles/search/profile-advanced-search-related-profile-options.model';
import { MessageDraftProfileSearch } from '../../shared/message-draft-profile-search.model';
import { MassCommMessageEditProfilesAddManualComponent } from './add-manual/mass-comm-message-edit-profiles-add-manual.component';
import { MassCommParameterService } from '../../shared/mass-comm-parameter.service';
import { InvoiceAdvancedSearch } from '../../../revenue/shared/invoice/invoice-advanced-search.model';
import { InvoiceSelectorComponent } from '../../../revenue/invoice-selector/invoice-selector.component';
import { ProfileSearchSummaryComponent } from '../../../profiles/search/profile-search-summary/profile-search-summary.component';
import { GuidService } from '../../../shared/util/guid.service';

/**
 * edit a single mass comm message
 */
@Component({
  selector: 'wl-mass-comm-message-edit-profiles',
  templateUrl: 'mass-comm-message-edit-profiles.component.html',
  styleUrls: ['mass-comm-message-edit-profiles.component.less'],
  providers: [ProfileSearchService, MassCommParameterService],
    standalone: false
})
export class MassCommMessageEditProfilesComponent implements OnInit {
  @Input() public get message(): Message {
    return this._message;
  }
  public set message(value: Message) {
    this._message = value;
  }

  @Input() public readonly = false;
  @Input() public isDraft = false;
  @Input() public isResend = false;

  @Output() public validChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @ViewChild('recipientGrid', { static: false }) private _recipientGrid: PrimeDataTable;
  @ViewChild('manuallyAddedRecipientGrid', { static: false }) private _manuallyAddedRecipientGrid: PrimeDataTable;
  @ViewChild('profileSearchParamsGrid', { static: false }) private _profileSearchParamsGrid: PrimeDataTable;
  @ViewChild('doNotEmailProfilesGrid', { static: false }) private _doNotEmailProfilesGrid: PrimeDataTable;
  @ViewChild('addManualRecipients', { static: false }) private _addManualRecipients: MassCommMessageEditProfilesAddManualComponent;
  @ViewChild('invoiceSelector', { static: false }) private _invoiceSelector: InvoiceSelectorComponent;
  @ViewChild('searchSummary', { static: false }) private _searchSummary: ProfileSearchSummaryComponent;

  private _message: Message = null;
  public loading = false;
  public isSearching = false;
  public toAction = '';
  public gridAction = '';
  public previewingProfilesAdvancedSearch = false;
  public checkingProfileDoNotSent = false;
  public gridActionBusy = false;
  public showSummaries = false;
  public useMainContact = false;
  public useBillingContactFallThrough = false;
  public useInvoiceContact = false;
  public overwriteBlankOnly = false;
  public useEmailTypeSelection = false;

  public searchParams: any = {};
  public recipientResult: PagedResult<Array<DraftProfileMin | any>> = new PagedResult<Array<DraftProfileMin | any>>([]);
  public profileSearchParamsResult: PagedResult<Array<DraftProfileMin>> = new PagedResult<Array<DraftProfileMin>>();
  public doNotEmailProfilesResult: PagedResult<Array<DraftProfileMin>> = new PagedResult<Array<DraftProfileMin>>();
  public doNotEmailProfileSearchParams: MessageDraftProfileSearch = new MessageDraftProfileSearch();
  public loadingDoNotEmailProfiles = false;
  public gettingAllDoNotEmailProfileIds = false;
  public gettingAllDoNotEmailProfileIdError = '';

  public recipientsSelectedAll = false;
  public recipientsManuallyChecked: Array<number> = [];

  public manualRecipientsSelectedAll = false;
  public manualRecipientsManuallyChecked: Array<ManualRecipient> = [];

  public doNotEmailProfilesOverrideSelectedAll = false;
  public doNotEmailProfilesOverrideManuallyChecked: Array<number> = [];

  public profileAdvancedSearchEnabled = false;
  public profileSearchParams: ProfileAdvancedSearchParams = new ProfileAdvancedSearchParams();
  public previewProfileSelectorParams: MessageDraftProfileSearch = new MessageDraftProfileSearch();
  public profileSearchParamsModalError = '';
  public previewProfileSelectorSearched = false;

  public commonDataViewProfileAdvancedSearchParams: ProfileAdvancedSearchParams = null;

  public replaceEmailAddressOptionValues: object = ReplaceEmailAddressOptionEnum;
  private _defaultEmailAddressOptionValue: ReplaceEmailAddressOptionEnum = ReplaceEmailAddressOptionEnum.None;

  private profileAdvanceSearchMap: Map<string, ProfileAdvancedSearchParams | InvoiceAdvancedSearch> = new Map<string, ProfileAdvancedSearchParams | InvoiceAdvancedSearch>();
  private invoiceAdvanceSearchMap: Map<string, ProfileAdvancedSearchParams | InvoiceAdvancedSearch> = new Map<string, ProfileAdvancedSearchParams | InvoiceAdvancedSearch>();

  private _autoLaunchEmailChooserFirstTime = true;
  public editingEmailTypeConfig: ReplaceWithEmailTypeConfig = new ReplaceWithEmailTypeConfig();
  public emailTypes: { [key: number]: string } = {};

  // invoice selector stuff
  public revenueModuleReadAccess = false;
  public invoiceSelectorEnabled = false;
  public invoiceSelectorSearchParams: InvoiceAdvancedSearch = new InvoiceAdvancedSearch();
  public isInvoiceSelectorSearching = false;
  public invoiceSelectorModalError = '';

  public showSearchParamsModal = false;
  public showDoNotEmailModal = false;
  public showEmailTypeModal = false;
  public showInvoiceSelectorModal = false;
  public showClearSearchModal = false;

  /**
   * inject
   */
  public constructor(
    private _userService: UserService,
    private _massCommService: MassCommService,
    private _profileSearchService: ProfileSearchService,
    private _datagridService: DatagridService,
    private _navigationService: NavigationService,
    private _profileService: ProfileService,
    private _massCommParameterService: MassCommParameterService,
    private _observationService: ObservationService,
    private _guidService: GuidService,
  ) {
    this.recipientResult.CurrentPageNumber = 1;
    this.recipientResult.TotalCount = 0;
    this.recipientResult.TotalPages = 0;
  }

  /**
   * init
   */
  public ngOnInit(): void {
    this.setupComponent();
  }

  /**
   * setup
   */
  private async setupComponent(): Promise<void> {
    // deactivate revenue stuff if they're not allowed to see it
    this._userService.getCurrentUserSecurityElementAccessLevel('Revenue_Module').then((revenueMofuleUserAccessLevel: number) => {
      this.revenueModuleReadAccess = revenueMofuleUserAccessLevel > 0;
    });

    let profileEmailTypesResult = await this._profileService.searchEmailTypes({ PageNumber: 0, PageSize: 0, OrderBy: 'Name' });

    // convert list of objects to key/value keyed on address type id
    if (profileEmailTypesResult && profileEmailTypesResult.Result) {
      profileEmailTypesResult.Result.forEach((x) => (this.emailTypes[x.EmailAddressTypeID] = x.Name));
    }

    if (this._massCommParameterService.hasParams()) {
      if (this._massCommParameterService.hasProfileIdParams()) {
        let searchParams = new ProfileAdvancedSearchParams();
        searchParams.PermitProfiles = this._massCommParameterService.getProfileIdParams();
        this.resetMessageProfileSearchParams();
        this.pushAdvancedSearchToSearchParams(searchParams);

        this.message.ProfileSearchParams.ReplaceEmailEventId = this._massCommParameterService.getEventId();
        this._defaultEmailAddressOptionValue =
          this._massCommParameterService.getFromType() == 'registrations'
            ? ReplaceEmailAddressOptionEnum.EventRegistration
            : ReplaceEmailAddressOptionEnum.EventAttendee;
        this.message.ProfileSearchParams.ReplaceEmailAddressOption = this._defaultEmailAddressOptionValue;
        this.startSearch();
        this._massCommParameterService.clearProfileIdParams();
      }

      if (this._massCommParameterService.hasEmailParams()) {
        let paramEmails = this._massCommParameterService.getEmailParams();
        this.message.ManualRecipients = new Array<ManualRecipient>();
        for (let nv of paramEmails) {
          let mailReceipient = new ManualRecipient();
          mailReceipient.Name = nv.name;
          mailReceipient.Email = nv.value;
          this.message.ManualRecipients.push(mailReceipient);
        }
        this._massCommParameterService.clearEmailParams();
      }

      if (this._massCommParameterService.hasProfileSearchParams()) {
        const params = this._massCommParameterService.getProfileSearchParams();
        this.resetMessageProfileSearchParams();
        this.pushAdvancedSearchToSearchParams(params);
        this.message.ProfileSearchParams.UnionProfileSearches.forEach((x) => {
          this.checkProfilesDoNotEmailSettings(JSON.parse(JSON.stringify(x)), true);
        });
        this.startSearch();
        this._massCommParameterService.clearProfileSearchParams();
      }
    }
  }

  /**
   * called when the validity of this form is changed
   * @param newValidity
   */
  public handleFormValidChanged(newValidity: boolean) {
    this.validChange.emit(newValidity);
  }

  /**
   * start a search
   */
  public startSearch(): void {
    this.recipientsSelectedAll = false;
    this.recipientsManuallyChecked = [];
    this._recipientGrid.reset();
  }

  /**
   * for the ngprime data table
   */
  public lazyLoadRecipientProfiles($event: any): void {
    if (this.isDraft) {
      if (this.message.ProfileSearchParams) {
        this._datagridService.mapDatagridPagingToPagedSearch($event, this.message.ProfileSearchParams);
      }
    } else {
      this._datagridService.mapDatagridPagingToPagedSearch($event, this.searchParams);
    }

    if (!this.isSearching) {
      this.getRecipients();
    }
  }

  /**
   * search for recipients
   */
  public getRecipients() {
    if (!this.message || this.isSearching) {
      return;
    }

    if (this.isDraft) {
      if (this.message.ProfileSearchParams) {
        // Merge Ignored Profiles and assign to all Profile Searches
        if (this.message.ProfileSearchParams.UnionProfileSearches.length > 1) {
          let allIgnoredProfiles = this.message.ProfileSearchParams.UnionProfileSearches.map((x) => x.IgnoreProfiles).flat();
          this.message.ProfileSearchParams.UnionProfileSearches.forEach((search) => {
            // updating search params, no need for new GUID
            search.IgnoreProfiles = allIgnoredProfiles;
          });
        }

        this.isSearching = true;

        // profile search
        this._massCommService.searchDraftProfiles(this.message.ProfileSearchParams).then(
          (result) => {
            this._datagridService.mapPagedResultsToPagedSearch(result, this.message.ProfileSearchParams);
            this.recipientResult.Result = result.Result.map((profile) => {
              let selectedException: boolean = this.recipientsManuallyChecked.indexOf(profile.ProfileId) > -1;

              return {
                selected: this.recipientsSelectedAll ? !selectedException : selectedException,
                ProfileId: profile.ProfileId,
                Member: profile.Member,
                OrgInd: profile.OrgInd,
                Name: profile.ReportName,
                MemberTypeIconColor: profile.MemberTypeIconColor,
                Email: profile.Email,
                EmailType: profile.EmailType,
              };
            });

            this.recipientResult.TotalCount = result.TotalCount;
            this.recipientResult.TotalPages = result.TotalPages;
            this.recipientResult.PageSize = result.PageSize;
            this.profileSearchParams.TotalCount = this.recipientResult.TotalCount;
            this._datagridService.mapPagedResultsToPagedSearch(this.recipientResult, this.searchParams);
            this.isSearching = false;
          },
          () => (this.isSearching = false)
        );
      } else {
        // makes the grid stop loading the first time
        this.recipientResult.Result = [];
        this.recipientResult.TotalCount = 0;
        this.recipientResult.TotalPages = 0;
        this.recipientResult.CurrentPageNumber = 1;
      }
    } else if (this.message.MessageId > 0) {
      this.searchParams.MessageId = this.message.MessageId;

      this.isSearching = true;

      this._massCommService.searchCommunicationRecords(this.searchParams).then(
        (result) => {
          this._datagridService.mapPagedResultsToPagedSearch(result, this.searchParams);

          this.recipientResult = result;

          this.recipientResult.Result = result.Result.map((profile) => {
            return {
              ProfileId: profile.ProfileId,
              Member: profile.Member,
              OrgInd: profile.OrgInd,
              Name: profile.SendToDisplayName,
              Email: profile.SendTo,
            };
          });

          this.isSearching = false;
        },
        () => (this.isSearching = false)
      );
    }
  }

  /**
   * to action doer
   */
  public handleToActionChange(): void {
    this.commonDataViewProfileAdvancedSearchParams = null;

    switch (this.toAction) {
      case 'newListFromProfileSelector':
        this.openProfileSearchModal();
        break;
      case 'newListFromInvoiceSelector':
        this.openInvoiceSelectorModal();
        break;
      case 'selectSavedDataView':
        this.commonDataViewProfileAdvancedSearchParams = new ProfileAdvancedSearchParams();
        break;
      case 'addManualEmail':
        this.openAddManualRecipient();
        break;
    }

    // reset dropdown
    setTimeout(() => (this.toAction = ''));
  }

  public handleCommonDataViewStartSearch(selectedEvent: Event) {
    this.checkProfilesDoNotEmailSettings(JSON.parse(JSON.stringify(this.commonDataViewProfileAdvancedSearchParams)));
  }

  public handleCommonDataViewClear(selectedEvent: Event) {
    this.commonDataViewProfileAdvancedSearchParams = null;
  }

  /**
   * grid action doer
   */
  public executeGridAction(): void {
    switch (this.gridAction) {
      case 'removeSelected':
        this.handleRemoveSelected();
        break;
      case 'exportToExcel':
        this.exportProfilestoExcel();
        break;
    }
  }

  public useBillingContactFallThroughChanged() {
    if (!this.useBillingContactFallThrough) {
      this.useInvoiceContact = false;
      this.overwriteBlankOnly = false;
    }
  }

  /**
   * remove selected from the main grid of recipients
   */
  private handleRemoveSelected() {
    if (this.recipientsSelectedAll) {
      if (!this.recipientsManuallyChecked.length) {
        // easy remove all
        this.resetMessageProfileSearchParams(true);

        // if they came from events, reset this to none, so it's no longer an event registration/attendee search
        this._defaultEmailAddressOptionValue = ReplaceEmailAddressOptionEnum.None;
      } else if (this.message.ProfileSearchParams.UnionInvoiceSearches.length > 0) {
        let invoiceSearchParams = new InvoiceAdvancedSearch();
        invoiceSearchParams.PermitProfiles = [].concat(this.recipientsManuallyChecked);
        this.resetMessageProfileSearchParams();
        this.pushAdvancedSearchToSearchParams(invoiceSearchParams);
      } else {
        // keep just the unchecked ones
        let profileSearchParams = new ProfileAdvancedSearchParams();
        profileSearchParams.PermitProfiles = [].concat(this.recipientsManuallyChecked);

        this.resetMessageProfileSearchParams();
        this.pushAdvancedSearchToSearchParams(profileSearchParams);
      }

      this.startSearch();
    } else if (this.recipientsManuallyChecked.length) {
      if (this.message.ProfileSearchParams.UnionProfileSearches) {
        this.message.ProfileSearchParams.UnionProfileSearches.forEach((profileSearchParams) => {
          // updating search params, no need for new GUID
          profileSearchParams.IgnoreProfiles = profileSearchParams.IgnoreProfiles.concat(
            this.recipientsManuallyChecked.filter((x) => profileSearchParams.IgnoreProfiles.indexOf(x) < 0)
          );
        });
      }

      if (this.message.ProfileSearchParams.UnionInvoiceSearches) {
        this.message.ProfileSearchParams.UnionInvoiceSearches.forEach((invoiceSearchParams) => {
          // updating search params, no need for new GUID
          invoiceSearchParams.IgnoreProfiles = invoiceSearchParams.IgnoreProfiles.concat(
            this.recipientsManuallyChecked.filter((x) => invoiceSearchParams.IgnoreProfiles.indexOf(x) < 0)
          );
        });
      }

      this.startSearch();
    }

    this.recipientsSelectedAll = false;
    this.recipientsManuallyChecked = [];
  }

  /**
   * export profiles and manual recipients to excel
   */
  private async exportProfilestoExcel(): Promise<void> {
    if (this.gridActionBusy) {
      return;
    }

    this.gridActionBusy = true;

    try {
      let url = '';

      if (this.isDraft) {
        url = await this._massCommService.exportSearchDraftsAsExcel(this.message);
      } else {
        url = await this._massCommService.exportCommunicationRecordsAsExcel(this.searchParams);
      }

      this._navigationService.popupSafeNavigate(url);
    } catch (e) {
    } finally {
      this.gridActionBusy = false;
    }
  }

  /**
   * preps and opens the profile search window
   */
  private openProfileSearchModal(): void {
    if (!this.message || !this.isDraft) {
      return;
    }

    this.profileAdvancedSearchEnabled = true;

    this.resetProfileSearchModalParams();

    this.showSearchParamsModal = true;
  }

  /**
   * preps and opens the invoice search window
   */
  private openInvoiceSelectorModal(): void {
    if (!this.message || !this.isDraft) {
      return;
    }

    this.invoiceSelectorEnabled = true;
    this.resetInvoiceSelectorModalParams();
    this.showInvoiceSelectorModal = true;
  }

  /**
   * resets the profile search window
   */
  private resetProfileSearchModalParams(): void {
    // setup new empty search for profile selector
    this.profileSearchParams = new ProfileAdvancedSearchParams();
    this.previewProfileSelectorParams = new MessageDraftProfileSearch();
    this.previewProfileSelectorParams.UnionProfileSearches = [this.profileSearchParams];
    this.previewProfileSelectorParams.OrderBy = 'ReportName:asc';
    this.previewProfileSelectorSearched = false;
    if (this._searchSummary) {
      this._searchSummary.hideSummary();
    }

    setTimeout(() => {
      this.profileSearchParamsResult.Result = [];
      this.profileSearchParamsResult.TotalCount = 0;
      this.profileSearchParamsResult.TotalPages = 0;
      this.profileSearchParamsResult.CurrentPageNumber = 1;
      this._profileSearchParamsGrid.sortField = 'ReportName';
      this._profileSearchParamsGrid.sortOrder = 1;
    });

    this.profileSearchParamsModalError = '';
  }

  /**
   * resets the invoice selector window
   */
  private resetInvoiceSelectorModalParams(): void {
    // setup new empty search for profile selector
    this.invoiceSelectorSearchParams = new InvoiceAdvancedSearch();
    this.invoiceSelectorModalError = '';
    setTimeout(() => this._invoiceSelector.resetResultGrid());
  }

  /**
   * radio button unchecker.
   * Auto launch email type chooser if they pick email types and it's the first time it opened this edit.
   * @param $event
   * @param newValue
   */
  public handleMassCommReplaceTypeClick(): void {
    // support unchecking radio buttons
    if (this.useMainContact) {
      this._message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.MainContact;
    } else if (this.useBillingContactFallThrough) {
      if (this.useInvoiceContact) {
        this._message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.InvoiceContact;
      } else {
        this._message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.BillingContact;
      }
    } else if (this.useEmailTypeSelection) {
      this._message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.EmailType;
      this.autoLaunchEmailTypeChooser();
    } else {
      this._message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.None;
    }

    // search
    setTimeout(() => this.getRecipients());
  }

  /**
   * only if profile email blank checkbox clicked
   */
  public handleReplaceIfProfileEmailBlankClick(): void {
    // search
    setTimeout(() => this.getRecipients());
  }

  /**
   * open email type config automatically, but only if they've never opened it
   */
  private autoLaunchEmailTypeChooser(): void {
    // only do once automatically
    if (!this._autoLaunchEmailChooserFirstTime) {
      return;
    }

    this.launchEmailTypeChooser();
  }

  /**
   * open email type config
   */
  public launchEmailTypeChooser(): void {
    // not while searching
    if (this.isSearching) {
      return;
    }

    this._autoLaunchEmailChooserFirstTime = false;

    this.editingEmailTypeConfig = new ReplaceWithEmailTypeConfig();

    // copy so they can cancel
    if (this._message && this._message.ProfileSearchParams && this._message.ProfileSearchParams.ReplaceWithEmailTypeConfig) {
      this.editingEmailTypeConfig = JSON.parse(JSON.stringify(this._message.ProfileSearchParams.ReplaceWithEmailTypeConfig));
    }

    this.showEmailTypeModal = true;
  }

  /**
   * user accepted email type config
   */
  public accepEmailTypeConfigClick(): void {
    if (!this._message.ProfileSearchParams) {
      this.resetMessageProfileSearchParams();
      this._message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.EmailType;
    }

    // copy
    this._message.ProfileSearchParams.ReplaceWithEmailTypeConfig = JSON.parse(JSON.stringify(this.editingEmailTypeConfig));

    this.getRecipients();
    this.showEmailTypeModal = false;
  }

  /**
   * lazyload of the profile selector grid
   */
  public lazyLoadProfileSearchParamsPreview($event: any): void {
    if (!this.previewProfileSelectorSearched) {
      return;
    }

    this._datagridService.mapDatagridPagingToPagedSearch($event, this.previewProfileSelectorParams);

    if (!this.previewingProfilesAdvancedSearch) {
      this.getProfileAdvancedSearchPreview();
    }
  }

  /**
   * do the search for profile selector
   */
  private async getProfileAdvancedSearchPreview(): Promise<void> {
    if (!this.isDraft || this.previewingProfilesAdvancedSearch) {
      return;
    }

    this.profileSearchParamsModalError = '';
    this.previewingProfilesAdvancedSearch = true;
    this.previewProfileSelectorSearched = true;

    // profile search
    try {
      let result = await this._massCommService.searchDraftProfiles(this.previewProfileSelectorParams);

      this._datagridService.mapPagedResultsToPagedSearch(result, this.previewProfileSelectorParams);
      this.profileSearchParamsResult = result;
    } catch (ex) {
      this.profileSearchParamsModalError = ex && ex.Message ? ex.Messge : ex;
    } finally {
      this.previewingProfilesAdvancedSearch = false;
    }
  }

  /**
   * handle when the user clicks the profile search preview
   */
  public previewProfileSearchParamsClick(): void {
    this.previewProfileSelectorSearched = true;
    this.startPreviewProfileSearch();
  }

  /**
   * handle when the user clicks to clear profile search params
   */
  public previewProfileClearParamsClick(): void {
    this.resetProfileSearchModalParams();
  }

  /**
   * start profile search for profile selector modal
   */
  private startPreviewProfileSearch(): void {
    let oldSortField: string = this._profileSearchParamsGrid.sortField;
    let oldSortOrder: number = this._profileSearchParamsGrid.sortOrder;

    this._profileSearchParamsGrid.reset();

    this._profileSearchParamsGrid.sortField = oldSortField;
    this._profileSearchParamsGrid.sortOrder = oldSortOrder;
  }

  /**
   * handle when the user accepts the profile search criteria for this draft
   */
  public async acceptProfileSearchParamsClick(): Promise<void> {
    this.profileSearchParamsModalError = '';
    this.checkProfilesDoNotEmailSettings(JSON.parse(JSON.stringify(this.profileSearchParams)));
    if (this._searchSummary) {
      this._searchSummary.refreshHeader();
    }
  }

  /**
   * handle when the user accepts the wants to run an invoise selector search
   */
  public invoiceSelectorSearch(): void {
    this.invoiceSelectorModalError = '';
    this._invoiceSelector.search();
  }

  /**
   * handle when the user accepts the invoice selector criteria for this draft
   */
  public async acceptInvoiceSelectorClick(): Promise<void> {
    this.invoiceSelectorModalError = '';

    // initialize the search params. this happens further up the stack too.
    if (!this.message.ProfileSearchParams || !this.message.ProfileSearchParams.UnionProfileSearches) {
      this.resetMessageProfileSearchParams();
    }

    if (this.useInvoiceContact) {
      this.message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.InvoiceContact;
    } else if (this.useBillingContactFallThrough) {
      this.message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.BillingContact;
    } else if (
      this.message.ProfileSearchParams.ReplaceEmailAddressOption === ReplaceEmailAddressOptionEnum.BillingContact ||
      this.message.ProfileSearchParams.ReplaceEmailAddressOption === ReplaceEmailAddressOptionEnum.InvoiceContact
    ) {
      this.message.ProfileSearchParams.ReplaceEmailAddressOption = this._defaultEmailAddressOptionValue;
    }

    this.message.ProfileSearchParams.ReplaceEmailOnlyIfPrimaryEmailIsBlank = this.overwriteBlankOnly;

    this.checkProfilesDoNotEmailSettings(JSON.parse(JSON.stringify(this.invoiceSelectorSearchParams)));
  }

  /**
   * check for profiles with "do not email" on, and show a modal with manual overrides if there are any.
   * If not, saves to draft message's profile search.
   */
  private async checkProfilesDoNotEmailSettings(
    params: ProfileAdvancedSearchParams | InvoiceAdvancedSearch,
    isProfileSelectorFlow: boolean = false
  ): Promise<void> {
    if (this.checkingProfileDoNotSent) {
      return;
    }

    if (!params) {
      return;
    }

    this.checkingProfileDoNotSent = true;
    this.doNotEmailProfileSearchParams = new MessageDraftProfileSearch();

    if ((<ProfileAdvancedSearchParams>params).hasOwnProperty('ContactInfoSearchExpressions')) {
      this.doNotEmailProfileSearchParams.UnionProfileSearches = [<ProfileAdvancedSearchParams>params];
    }

    if ((<InvoiceAdvancedSearch>params).hasOwnProperty('InvoiceInfoSearchExpressions')) {
      this.doNotEmailProfileSearchParams.UnionInvoiceSearches = [<InvoiceAdvancedSearch>params];
    }

    this.doNotEmailProfileSearchParams.PageSize = 1;
    this.doNotEmailProfileSearchParams.PageNumber = 1;

    let doNotEmailProfilesPromise = this._massCommService.getDoNotEmailProfiles(this.doNotEmailProfileSearchParams);

    // reset select all stuff
    this.recipientsManuallyChecked = [];
    this.recipientsSelectedAll = false;

    try {
      let doNotEmailProfilesResult = await doNotEmailProfilesPromise;

      // all profiles like being emailed
      if (!doNotEmailProfilesResult.TotalCount) {
        if (
          !this.message.ProfileSearchParams ||
          !this.message.ProfileSearchParams.UnionProfileSearches ||
          !this.message.ProfileSearchParams.UnionInvoiceSearches
        ) {
          this.resetMessageProfileSearchParams();
        }

        // copy
        if ((<ProfileAdvancedSearchParams>params).hasOwnProperty('ContactInfoSearchExpressions')) {
          this.pushAdvancedSearchToSearchParams(params);
        }

        if ((<InvoiceAdvancedSearch>params).hasOwnProperty('InvoiceInfoSearchExpressions')) {
          this.pushAdvancedSearchToSearchParams(params);
        }

        this.showSearchParamsModal = false;
        this.showInvoiceSelectorModal = false;

        // if came from common data view selector, unselect it since it's done
        this.commonDataViewProfileAdvancedSearchParams = null;

        this.startSearch();
      } else {
        this.doNotEmailProfilesResult = new PagedResult<Array<DraftProfileMin>>();

        // tell them about profiles opting out of email communication
        this.doNotEmailProfileSearchParams.PageSize = 20;
        this.showSearchParamsModal = false;
        this.showInvoiceSelectorModal = false;

        if (isProfileSelectorFlow) {
          this._observationService.massCommProfilesTabSelected();
        }

        this.openProfilesThatDoNotWantEmailsPrompt();
      }
    } catch (ex) {
      this.profileSearchParamsModalError = this.invoiceSelectorModalError = ex && ex.Message ? ex.Message : ex;
    } finally {
      this.checkingProfileDoNotSent = false;
    }
  }

  /**
   * opens the window for deciding what to do with profiles that are marked "do not email"
   */
  public openProfilesThatDoNotWantEmailsPrompt(): void {
    this.doNotEmailProfilesOverrideSelectedAll = false;
    this.doNotEmailProfilesOverrideManuallyChecked = [];
    this.gettingAllDoNotEmailProfileIds = false;
    this.gettingAllDoNotEmailProfileIdError = '';
    this._doNotEmailProfilesGrid.reset();
    this.showDoNotEmailModal = true;
  }

  /**
   * lazyload of the "do not email" profile selector grid
   */
  public lazyLoadDoNotEmailProfiles($event: any): void {
    this._datagridService.mapDatagridPagingToPagedSearch($event, this.doNotEmailProfileSearchParams);

    if (!this.previewingProfilesAdvancedSearch) {
      this.getDoNotEmailProfiles();
    }
  }

  /**
   * load profiles that match the profile search and have "do not email" checked
   */
  private async getDoNotEmailProfiles(): Promise<void> {
    this.loadingDoNotEmailProfiles = true;

    // profile search
    try {
      let result = await this._massCommService.getDoNotEmailProfiles(this.doNotEmailProfileSearchParams);
      if (result && result.Result) {
        result.Result.forEach((x) => {
          let selectedException: boolean = this.doNotEmailProfilesOverrideManuallyChecked.indexOf(x.ProfileId) > -1;
          (<any>x).selected = this.doNotEmailProfilesOverrideSelectedAll ? !selectedException : selectedException;
        });
      }

      this._datagridService.mapPagedResultsToPagedSearch(result, this.doNotEmailProfileSearchParams);
      this.doNotEmailProfilesResult = result;
    } catch (e) {
    } finally {
      this.loadingDoNotEmailProfiles = false;
    }
  }

  /**
   * user clicked "continue" on the override "do not email" profiles window
   */
  public async acceptDoNotEmailProfilesClick(): Promise<void> {
    if (this.gettingAllDoNotEmailProfileIds) {
      return;
    }

    // if they overide them all, then you have to pull out the ones they didn't select
    if (!this.doNotEmailProfilesOverrideSelectedAll) {
      this.gettingAllDoNotEmailProfileIds = true;
      this.gettingAllDoNotEmailProfileIdError = '';

      try {
        let doNotEmailProfileIds = await this._massCommService.getDoNotEmailProfileIds(this.doNotEmailProfileSearchParams);

        // manually excluded (so included)
        if (this.doNotEmailProfilesOverrideManuallyChecked && this.doNotEmailProfilesOverrideManuallyChecked.length) {
          doNotEmailProfileIds = doNotEmailProfileIds.filter((id) => this.doNotEmailProfilesOverrideManuallyChecked.indexOf(id) < 0);
        }

        // copy to draft message
        if (this.doNotEmailProfileSearchParams.UnionProfileSearches && this.doNotEmailProfileSearchParams.UnionProfileSearches.length) {
          // profile search
          let newProfileSearchParams = <ProfileAdvancedSearchParams>(
            JSON.parse(JSON.stringify(this.doNotEmailProfileSearchParams.UnionProfileSearches[0]))
          );
          newProfileSearchParams.IgnoreProfiles = [].concat(doNotEmailProfileIds);

          if (!this.message.ProfileSearchParams) {
            this.resetMessageProfileSearchParams();
          }

          if (!this.message.ProfileSearchParams.UnionProfileSearches) {
            this.profileAdvanceSearchMap.clear();
            this.message.ProfileSearchParams.UnionProfileSearches = [];
          }
          if (newProfileSearchParams.Guid && this.profileAdvanceSearchMap.has(newProfileSearchParams.Guid)) {
            (<ProfileAdvancedSearchParams>this.profileAdvanceSearchMap.get(newProfileSearchParams.Guid)).IgnoreProfiles = newProfileSearchParams.IgnoreProfiles;
          } else {
            this.pushAdvancedSearchToSearchParams(newProfileSearchParams);
          }
        }

        if (this.doNotEmailProfileSearchParams.UnionInvoiceSearches && this.doNotEmailProfileSearchParams.UnionInvoiceSearches.length) {
          // was invoice search
          let newInvoiceSearchParams = <InvoiceAdvancedSearch>(
            JSON.parse(JSON.stringify(this.doNotEmailProfileSearchParams.UnionInvoiceSearches[0]))
          );
          newInvoiceSearchParams.IgnoreProfiles = [].concat(doNotEmailProfileIds);

          if (!this.message.ProfileSearchParams) {
            this.resetMessageProfileSearchParams();
          }

          if (!this.message.ProfileSearchParams.UnionInvoiceSearches) {
            this.invoiceAdvanceSearchMap.clear();
            this.message.ProfileSearchParams.UnionInvoiceSearches = [];
          }

          if (this.useInvoiceContact) {
            this.message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.InvoiceContact;
          } else if (this.useBillingContactFallThrough) {
            this.message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.BillingContact;
          }

          if (newInvoiceSearchParams.Guid && this.invoiceAdvanceSearchMap.has(newInvoiceSearchParams.Guid)) {
            (<InvoiceAdvancedSearch>this.invoiceAdvanceSearchMap.get(newInvoiceSearchParams.Guid)).IgnoreProfiles = newInvoiceSearchParams.IgnoreProfiles;
          } else {
            this.pushAdvancedSearchToSearchParams(newInvoiceSearchParams);
          }
        }

        // if came from common data view selector, unselect it since it's done
        this.commonDataViewProfileAdvancedSearchParams = null;

        this.showDoNotEmailModal = false;
        this.startSearch();
      } catch (ex) {
        this.gettingAllDoNotEmailProfileIdError = ex && ex.Message ? ex.Message : ex;
      } finally {
        this.gettingAllDoNotEmailProfileIds = false;
      }
    } else {
      // copy to draft message
      if (this.doNotEmailProfileSearchParams.UnionProfileSearches && this.doNotEmailProfileSearchParams.UnionProfileSearches.length) {
        // profile search
        let newProfileSearchParams = <ProfileAdvancedSearchParams>(
          JSON.parse(JSON.stringify(this.doNotEmailProfileSearchParams.UnionProfileSearches[0]))
        );

        // manually excluded
        if (this.doNotEmailProfilesOverrideManuallyChecked && this.doNotEmailProfilesOverrideManuallyChecked.length) {
          newProfileSearchParams.IgnoreProfiles = [].concat(this.doNotEmailProfilesOverrideManuallyChecked);
        }

        if (!this.message.ProfileSearchParams || !this.message.ProfileSearchParams.UnionProfileSearches) {
          this.resetMessageProfileSearchParams();
        }

        if (newProfileSearchParams.Guid && this.profileAdvanceSearchMap.has(newProfileSearchParams.Guid)) {
          (<ProfileAdvancedSearchParams>this.profileAdvanceSearchMap.get(newProfileSearchParams.Guid)).IgnoreProfiles = newProfileSearchParams.IgnoreProfiles;
        } else {
          this.pushAdvancedSearchToSearchParams(newProfileSearchParams);
        }
      }

      if (this.doNotEmailProfileSearchParams.UnionInvoiceSearches && this.doNotEmailProfileSearchParams.UnionInvoiceSearches.length) {
        // invoice search
        let newInvoiceSearchParams = <InvoiceAdvancedSearch>(
          JSON.parse(JSON.stringify(this.doNotEmailProfileSearchParams.UnionInvoiceSearches[0]))
        );

        // manually excluded
        if (this.doNotEmailProfilesOverrideManuallyChecked && this.doNotEmailProfilesOverrideManuallyChecked.length) {
          newInvoiceSearchParams.IgnoreProfiles = [].concat(this.doNotEmailProfilesOverrideManuallyChecked);
        }

        if (!this.message.ProfileSearchParams || !this.message.ProfileSearchParams.UnionInvoiceSearches) {
          this.resetMessageProfileSearchParams();
        }

        if (newInvoiceSearchParams.Guid && this.invoiceAdvanceSearchMap.has(newInvoiceSearchParams.Guid)) {
          (<InvoiceAdvancedSearch>this.invoiceAdvanceSearchMap.get(newInvoiceSearchParams.Guid)).IgnoreProfiles = newInvoiceSearchParams.IgnoreProfiles;
        } else {
          this.pushAdvancedSearchToSearchParams(newInvoiceSearchParams);
        }

        if (this.useInvoiceContact) {
          this.message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.InvoiceContact;
        } else if (this.useBillingContactFallThrough) {
          this.message.ProfileSearchParams.ReplaceEmailAddressOption = ReplaceEmailAddressOptionEnum.BillingContact;
        }
      }

      // if came from common data view selector, unselect it since it's done
      this.commonDataViewProfileAdvancedSearchParams = null;

      this.showDoNotEmailModal = false;
      this.startSearch();
    }
  }

  /**
   * when a user clicks on "select all" checkbox in the main recipient grid
   * @param $event
   */
  public handleRecipientGridSelectAll($event: any): void {
    this.recipientsManuallyChecked = [];
    this.recipientResult.Result.forEach((x) => (x.selected = this.recipientsSelectedAll));
  }

  /**
   * when a user clicks on checkbox next to profile in the main recipient grid
   * @param $event
   * @param recipient
   */
  public handleRecipientSelected($event: any, recipient: any): void {
    if (this.recipientsManuallyChecked.some((x) => x == recipient.ProfileId)) {
      while (this.recipientsManuallyChecked.indexOf(recipient.ProfileId) > -1) {
        this.recipientsManuallyChecked.splice(this.recipientsManuallyChecked.indexOf(recipient.ProfileId), 1);
      }
    } else {
      this.recipientsManuallyChecked.push(recipient.ProfileId);
    }
  }

  /**
   * when a user clicks on "select all" checkbox in the manually added recipient grid
   * @param $event
   */
  public handleManualRecipientGridSelectAll($event: any): void {
    this.manualRecipientsManuallyChecked = [];
    this.message.ManualRecipients.forEach((x) => ((<any>x).selected = this.manualRecipientsSelectedAll));
  }

  /**
   * when a user clicks on checkbox next to profile in the manually added recipient grid
   * @param $event
   * @param recipient
   */
  public handleManualRecipientSelected($event: any, recipient: ManualRecipient): void {
    if (this.manualRecipientsManuallyChecked.some((x) => x == recipient)) {
      while (this.manualRecipientsManuallyChecked.indexOf(recipient) > -1) {
        this.manualRecipientsManuallyChecked.splice(this.manualRecipientsManuallyChecked.indexOf(recipient), 1);
      }
    } else {
      this.manualRecipientsManuallyChecked.push(recipient);
    }
  }

  /**
   * remove manual recipients
   */
  public removeSelectedManualRecipient(): void {
    if (this.manualRecipientsSelectedAll) {
      // keep unchecked, if nothing unchecked, then it's empty
      this.message.ManualRecipients = [].concat(this.manualRecipientsManuallyChecked);
    } else {
      // keep those not checked
      this.message.ManualRecipients = this.message.ManualRecipients.filter((x) => this.manualRecipientsManuallyChecked.indexOf(x) < 0);
    }

    this.manualRecipientsSelectedAll = false;
    this.message.ManualRecipients.forEach((x) => ((<any>x).selected = false));
    this.manualRecipientsManuallyChecked = [];
  }

  /**
   * when a user clicks on "select all" checkbox in "override do not email" window
   * @param $event
   */
  public handleDoNotEmailProfileGridSelectAll($event: any): void {
    this.doNotEmailProfilesOverrideManuallyChecked = [];
    this.doNotEmailProfilesResult.Result.forEach((x) => ((<any>x).selected = this.doNotEmailProfilesOverrideSelectedAll));
  }

  /**
   * when a user clicks on checkbox next to profile in "override do not email" window
   * @param $event
   * @param profile
   */
  public handleDoNotEmailProfileSelected($event: any, profile: any): void {
    if (this.doNotEmailProfilesOverrideManuallyChecked.some((x) => x == profile.ProfileId)) {
      while (this.doNotEmailProfilesOverrideManuallyChecked.indexOf(profile.ProfileId) > -1) {
        this.doNotEmailProfilesOverrideManuallyChecked.splice(this.doNotEmailProfilesOverrideManuallyChecked.indexOf(profile.ProfileId), 1);
      }
    } else {
      this.doNotEmailProfilesOverrideManuallyChecked.push(profile.ProfileId);
    }
  }

  /**
   * open the "add manual recipient" window
   */
  public openAddManualRecipient(): void {
    this._addManualRecipients.start();
  }

  /**
   * open the "add manual recipient" window
   */
  public handleNewManualRecipients(newRecipients: ManualRecipient[]): void {
    if (!this.message.ManualRecipients) {
      this.message.ManualRecipients = [];
    }

    this.message.ManualRecipients = this.message.ManualRecipients.concat(newRecipients);
    this.manualRecipientsSelectedAll = false;
    this.message.ManualRecipients.forEach((x) => ((<any>x).selected = false));
    this.manualRecipientsManuallyChecked = [];
  }

  public toggleSummariesVisible(): void {
    this.showSummaries = !this.showSummaries;
  }

  public confirmClearSearchCriteriaMessage(): void {
    this.showClearSearchModal = true;
  }

  public clearSearchCriteriaConfirmed(): void {
    this.resetMessageProfileSearchParams(true);
    this.getRecipients();

    this.toggleSummariesVisible();
    this.showClearSearchModal = false;
  }

  private pushAdvancedSearchToSearchParams(
    searchDto: ProfileAdvancedSearchParams | InvoiceAdvancedSearch): boolean {
    let searchType = this.getAdvancedSearchType(searchDto);

    if (!searchDto.Guid) {
      searchDto.Guid = this._guidService.getNewShortGuid();
    }

    if (searchType === 'profile' && !this.profileAdvanceSearchMap.has(searchDto.Guid)) {
      this.profileAdvanceSearchMap.set(searchDto.Guid, searchDto);
      this.message.ProfileSearchParams.UnionProfileSearches.push(<ProfileAdvancedSearchParams>searchDto);
    } else if (searchType === 'invoice' && !this.invoiceAdvanceSearchMap.has(searchDto.Guid)) {
      this.invoiceAdvanceSearchMap.set(searchDto.Guid, searchDto);
      this.message.ProfileSearchParams.UnionInvoiceSearches.push(<InvoiceAdvancedSearch>searchDto);
    }
    return;
  }

  private resetMessageProfileSearchParams(setAsNull: boolean = false): void {
    this.profileAdvanceSearchMap.clear();
    this.invoiceAdvanceSearchMap.clear();

    if (setAsNull) {
      this.message.ProfileSearchParams = null;
      return;
    }
    this.message.ProfileSearchParams = new MessageDraftProfileSearch();
    this.message.ProfileSearchParams.UnionProfileSearches = [];
    this.message.ProfileSearchParams.UnionInvoiceSearches = [];
  }

  private getAdvancedSearchType(params: ProfileAdvancedSearchParams | InvoiceAdvancedSearch): string {
    if ((<ProfileAdvancedSearchParams> params).hasOwnProperty('ContactInfoSearchExpressions') || (params instanceof (ProfileAdvancedSearchParams))) {
      return 'profile';
    }
    if (((<InvoiceAdvancedSearch> params).hasOwnProperty('InvoiceInfoSearchExpressions')) || (params instanceof (InvoiceAdvancedSearch))) {
      return 'invoice';
    }
    return '';
  }
}
